export default function RentRollDocumentation() {
  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-file-chart-line"></i>
            <span>Financial Documentation</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Rent Roll Documentation and Accuracy
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Your rent roll is one of the most scrutinized documents in any apartment building sale. Accuracy and completeness are essential for buyer confidence and smooth closing.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">What is a Rent Roll?</h3>
            
            <p className="text-slate-700 mb-6 leading-relaxed">
              A rent roll is a comprehensive report listing all units in your property, current tenants, lease terms, rent amounts, and payment status. It's the financial snapshot that buyers use to calculate property value, verify income, and assess risk. An accurate, detailed rent roll can make or break your sale.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-6">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-blue-600 text-2xl mt-1"></i>
                <div>
                  <h4 className="font-semibold text-blue-900 mb-2">Why Rent Rolls Matter</h4>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    Buyers use your rent roll to calculate NOI (Net Operating Income) and determine their maximum purchase price. Inaccuracies or missing information can delay closing, reduce offers by 10-20%, or even kill deals entirely.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">Essential Rent Roll Information</h3>

            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm">
                <i className="ri-home-4-line text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Unit Number and Type</p>
                  <p className="text-slate-600 text-xs">Complete unit identifier, bedroom/bathroom count, square footage, and unit features</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm">
                <i className="ri-user-3-line text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Tenant Name and Contact</p>
                  <p className="text-slate-600 text-xs">Full legal names of all tenants on lease, phone numbers, and email addresses</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm">
                <i className="ri-calendar-line text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Lease Start and End Dates</p>
                  <p className="text-slate-600 text-xs">Original lease commencement date, current lease expiration, and any renewal options</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm">
                <i className="ri-money-dollar-circle-line text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Current Monthly Rent</p>
                  <p className="text-slate-600 text-xs">Base rent amount, any additional charges (parking, storage, pets), and total monthly payment</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm">
                <i className="ri-safe-line text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Security Deposit Amount</p>
                  <p className="text-slate-600 text-xs">Total security deposit held, including any pet deposits or additional deposits</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm">
                <i className="ri-checkbox-circle-line text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Payment Status</p>
                  <p className="text-slate-600 text-xs">Current/delinquent status, amount of any arrears, and payment history notes</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm">
                <i className="ri-calendar-check-line text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Move-In Date</p>
                  <p className="text-slate-600 text-xs">Actual date tenant took possession, which may differ from lease start date</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm">
                <i className="ri-file-text-line text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Lease Type</p>
                  <p className="text-slate-600 text-xs">Fixed-term, month-to-month, or other lease structure</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm">
                <i className="ri-percent-line text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Rent Increases</p>
                  <p className="text-slate-600 text-xs">Scheduled rent increases, escalation clauses, or rent control restrictions</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm">
                <i className="ri-gift-line text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Concessions or Credits</p>
                  <p className="text-slate-600 text-xs">Any rent discounts, free months, or special arrangements granted to tenant</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/5a9ef0450583c7ba93981be430384a37.jpg"
                  alt="Rent roll documentation and financial records for apartment building sale"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-200">
              <h4 className="font-semibold text-slate-900 mb-4">Sample Rent Roll Summary</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                  <span className="text-slate-600">Total Units</span>
                  <span className="font-semibold text-slate-900">12</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                  <span className="text-slate-600">Occupied Units</span>
                  <span className="font-semibold text-slate-900">11 (91.7%)</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                  <span className="text-slate-600">Vacant Units</span>
                  <span className="font-semibold text-slate-900">1 (8.3%)</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                  <span className="text-slate-600">Total Monthly Rent</span>
                  <span className="font-semibold text-slate-900">$16,500</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                  <span className="text-slate-600">Average Rent per Unit</span>
                  <span className="font-semibold text-slate-900">$1,500</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                  <span className="text-slate-600">Total Security Deposits</span>
                  <span className="font-semibold text-slate-900">$16,500</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                  <span className="text-slate-600">Current on Rent</span>
                  <span className="font-semibold text-green-600">10 units (90.9%)</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-slate-200">
                  <span className="text-slate-600">Delinquent</span>
                  <span className="font-semibold text-red-600">1 unit (9.1%)</span>
                </div>
                <div className="flex justify-between items-center pt-2 bg-teal-50 p-3 rounded-lg">
                  <span className="font-semibold text-slate-900">Annual Gross Income</span>
                  <span className="font-bold text-teal-600 text-lg">$198,000</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6">
              <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <i className="ri-error-warning-line text-amber-600 text-xl"></i>
                Common Rent Roll Mistakes
              </h4>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-600 mt-0.5"></i>
                  <span className="text-slate-700">Showing market rent instead of actual rent collected</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-600 mt-0.5"></i>
                  <span className="text-slate-700">Omitting concessions or rent credits</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-600 mt-0.5"></i>
                  <span className="text-slate-700">Not updating payment status regularly</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-600 mt-0.5"></i>
                  <span className="text-slate-700">Missing lease expiration dates</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-600 mt-0.5"></i>
                  <span className="text-slate-700">Incorrect security deposit amounts</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-600 mt-0.5"></i>
                  <span className="text-slate-700">Not disclosing month-to-month tenancies</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 mb-12">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Rent Roll Verification Process</h3>
          
          <p className="text-slate-700 mb-6 leading-relaxed">
            Buyers will verify your rent roll accuracy during due diligence. Being proactive about verification demonstrates professionalism and builds buyer confidence. Here's how to prepare:
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center font-bold">
                  1
                </div>
                <h4 className="font-semibold text-slate-900">Cross-Reference with Leases</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Compare every line item on your rent roll against the actual signed lease agreements. Ensure rent amounts, dates, and terms match exactly.
              </p>
              <div className="bg-white p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Check:</strong> Rent amount, lease dates, security deposit, special provisions, and tenant names
                </p>
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center font-bold">
                  2
                </div>
                <h4 className="font-semibold text-slate-900">Verify Payment History</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Review bank statements and payment records for the past 12 months to confirm actual rent collected matches rent roll amounts.
              </p>
              <div className="bg-white p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Document:</strong> Any discrepancies between stated rent and actual payments received
                </p>
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center font-bold">
                  3
                </div>
                <h4 className="font-semibold text-slate-900">Confirm Security Deposits</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Reconcile security deposit ledger with bank account balances. Every deposit on the rent roll must be accounted for in your security deposit account.
              </p>
              <div className="bg-white p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Verify:</strong> Total deposits on rent roll = security deposit account balance
                </p>
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center font-bold">
                  4
                </div>
                <h4 className="font-semibold text-slate-900">Update Occupancy Status</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Ensure vacant units are clearly marked and occupied units show current tenant information. Update weekly during the sale process.
              </p>
              <div className="bg-white p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Frequency:</strong> Update rent roll every 7 days minimum during active marketing
                </p>
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center font-bold">
                  5
                </div>
                <h4 className="font-semibold text-slate-900">Document All Concessions</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                List every rent discount, free month, or special arrangement. Buyers need to know the true effective rent, not just the stated rent.
              </p>
              <div className="bg-white p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Include:</strong> Move-in specials, loyalty discounts, temporary reductions, and payment plans
                </p>
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-teal-600 text-white rounded-lg flex items-center justify-center font-bold">
                  6
                </div>
                <h4 className="font-semibold text-slate-900">Prepare Supporting Documentation</h4>
              </div>
              <p className="text-slate-600 text-sm mb-3">
                Organize all lease agreements, amendments, payment records, and correspondence in a due diligence folder for easy buyer access.
              </p>
              <div className="bg-white p-3 rounded-lg">
                <p className="text-xs text-slate-600">
                  <strong>Organize:</strong> By unit number with complete file for each tenant
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Red Flags Buyers Look For in Rent Rolls</h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-alert-line text-red-600 text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">High Delinquency Rate</h4>
              <p className="text-slate-600 text-sm mb-3">
                More than 10% of tenants behind on rent signals poor tenant quality or management issues.
              </p>
              <p className="text-xs text-slate-500 italic">
                Impact: 10-20% value reduction
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calendar-close-line text-red-600 text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">Multiple Expiring Leases</h4>
              <p className="text-slate-600 text-sm mb-3">
                More than 30% of leases expiring within 90 days creates turnover risk and vacancy concerns.
              </p>
              <p className="text-xs text-slate-500 italic">
                Impact: 5-15% value reduction
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-arrow-down-line text-red-600 text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">Below-Market Rents</h4>
              <p className="text-slate-600 text-sm mb-3">
                Rents 15%+ below market suggest deferred increases or problem tenants who can't afford market rates.
              </p>
              <p className="text-xs text-slate-500 italic">
                Impact: Reduces calculated NOI
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-gift-line text-red-600 text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">Excessive Concessions</h4>
              <p className="text-slate-600 text-sm mb-3">
                Multiple units with rent discounts or free months indicates weak demand or poor property condition.
              </p>
              <p className="text-xs text-slate-500 italic">
                Impact: Reduces effective rent
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calendar-event-line text-red-600 text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">High Month-to-Month %</h4>
              <p className="text-slate-600 text-sm mb-3">
                More than 20% of tenants on month-to-month creates income instability and turnover risk.
              </p>
              <p className="text-xs text-slate-500 italic">
                Impact: Reduces buyer confidence
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-question-line text-red-600 text-2xl"></i>
              </div>
              <h4 className="font-semibold text-slate-900 mb-3">Inconsistent Data</h4>
              <p className="text-slate-600 text-sm mb-3">
                Discrepancies between rent roll, leases, and bank statements raise serious credibility concerns.
              </p>
              <p className="text-xs text-slate-500 italic">
                Impact: Deal killer or major discount
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
